import { Fragment } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Link } from 'react-router';
import type { UseQueryResult } from '@tanstack/react-query';

import { Alert } from '@/components/ui/alert';
import { DataLoader } from '@/components/ui/DataLoader';
import type {
  LpTransferImpact,
  LpTransferRecalculatedDraft,
  LpTransferRecalculatedKind,
} from '@/types/investor';
import type { APIError } from '@/utils/api-client';

const RECALCULATED_DRAFT_PATHS: Record<
  LpTransferRecalculatedKind,
  (fundId: string, id: string) => string
> = {
  drawdown: (fundId, id) => `/app/funds/${fundId}/drawdowns/${id}/preview`,
  distribution: (fundId, id) => `/app/funds/${fundId}/distributions/${id}`,
  investment: (fundId, id) => `/app/funds/${fundId}/fund-transactions/${id}`,
};

export function LpTransferImpactSummary({
  fundId,
  query,
}: {
  fundId: string;
  query: UseQueryResult<LpTransferImpact, APIError>;
}) {
  // Pending also covers a query not yet enabled (edit inputs still settling),
  // which DataLoader would hand to its children as undefined.
  if (query.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking what this changes...
      </p>
    );
  }

  return (
    <DataLoader query={query} errorClassName="p-0">
      {(impact: LpTransferImpact) => (
        <ImpactDetails fundId={fundId} impact={impact} />
      )}
    </DataLoader>
  );
}

function ImpactDetails({
  fundId,
  impact,
}: {
  fundId: string;
  impact: LpTransferImpact;
}) {
  if (impact.blockers.length > 0) {
    return (
      <Alert variant="destructive" data-testid="lp-transfer-blockers">
        <AlertTriangle className="h-4 w-4" />
        <ul className="space-y-1 text-sm">
          {impact.blockers.map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  if (impact.recalculated.length === 0) return null;

  return (
    <p className="text-sm" data-testid="lp-transfer-recalculated">
      These drafts will be recalculated:{' '}
      {impact.recalculated.map((draft, index) => (
        <Fragment key={`${draft.kind}-${draft.id}`}>
          {index > 0 && ', '}
          <RecalculatedDraftLink fundId={fundId} draft={draft} />
        </Fragment>
      ))}
    </p>
  );
}

function RecalculatedDraftLink({
  fundId,
  draft,
}: {
  fundId: string;
  draft: LpTransferRecalculatedDraft;
}) {
  return (
    <Link
      to={RECALCULATED_DRAFT_PATHS[draft.kind](fundId, draft.id)}
      className="font-medium text-primary hover:underline"
    >
      {draft.label}
    </Link>
  );
}
